import { Injectable } from '@angular/core';
import { ApiService } from '../api.service';
import { Observable } from 'rxjs/Observable';
import { NotificationService } from '../../core/api/notification.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ConfigService } from '../../../app/config.service';
import { OAuthService } from 'angular-oauth2-oidc';

export interface IChannel {
  'id': number,
  'name': string,
  'tableName': string,
  'fieldDateReference': string,
  'fieldValue': string,
  'fieldRelationshipUc': string,
  'unit': string,
  'chartMarkerType': string,
  'chartMarkerColor': string,
  'chartVisibility': boolean,
  'secondaryAxis': boolean,
  'referenceChannel': boolean
}
export interface IChannelData {
  'referenceDate': string,
  'valueField': number,
  'referenceChannel': boolean
}
export interface IUcChannelData {
  'channelId': number,
  'channelData': Array<IChannelData>
}

@Injectable()
export class ChannelService {
  private requestHeaders: any;
  BASE_URL = ConfigService.app.baseUri;

  constructor(
    private apiService: ApiService,
    private oAuth: OAuthService,
    private notificationService: NotificationService,
    private httpClient: HttpClient,
  ) {
    this.apiService.init();
    this.requestHeaders = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.oAuth.getAccessToken()}`
    }
  }

  getChannels(): Observable<IChannel[]> {
    return this.apiService.client.channels.GET().map((res) => {
      return this.apiService.handleResponse(res).results;
    });
  }

  getUcChannels(ucid: number, seriesSince?: Date, seriesUntil?: Date): Observable<IUcChannelData[]> {
    let filter = ``;
    if (seriesSince) {
      filter += ` and DATE_REFERENCE ge \"${seriesSince.toISOString()}\"`;
    }
    if (seriesUntil) {
      filter += ` and DATE_REFERENCE le \"${seriesUntil.toISOString()}\"`;
    }

    return this.apiService.client.channels.ucdata.ucId(ucid.toString()).GET(filter).map((res) => {
      return this.apiService.handleResponse(res);
    })
  }

  getUcChannelsdata(channelid: number, ucid: number): Observable<IChannelData[]> {
    return this.apiService.client.channels.id(channelid.toString()).ucdata.ucId(ucid.toString()).GET().map((res) => {
      return this.apiService.handleResponse(res);
    });
  }

  getChannelsDetails(): Observable<any> {
    return this.apiService.client.channels.getchannelsdetail.GET().map((res) => {
      return this.apiService.handleResponse(res);
    });
  }

  getChannelPreview(channelId: number, type: number): Observable<any> {
    return this.apiService.client.channels.getchannelpreview.GET(channelId, type).map((res) => {
      return this.apiService.handleResponse(res);
    });
  }

  getChannelDataset(): Observable<any> {
    return this.apiService.client.channels.getchanneldataset.GET().map((res) => {
      return this.apiService.handleResponse(res);
    });
  }


  editChannelDataset(channelId, type, name): Observable<any> {
    return this.apiService.client.channels.editchannel.POST(channelId, type, name).map((res) => {
      return this.apiService.handleResponse(res);
    });
  }

  importChannelDataset(tableName, dataset): Observable<any> {
    return this.apiService.client.channels.importdataset.POST(tableName, dataset).map((res) => {
      return this.apiService.handleResponse(res);
    });
  }

  getdatasetcolumns(datasetId: number): Observable<any> {
    return this.apiService.client.channels.getdatasetcolumns.GET(datasetId).map((res) => {
      return this.apiService.handleResponse(res);
    });
  }

  createChannel(channelName, context): Observable<any> {
    return this.apiService.client.channels.createchannel.POST(channelName, context).map((res) => {
      return this.apiService.handleResponse(res);
    });
  }

  applyJoin(context, channelId, data): Observable<any> {
    return this.apiService.client.channels.applyjoin.POST(context, channelId, data).map((res) => {
      return this.apiService.handleResponse(res);
    });
  }

  keepRows(tablesName: Array<string>[]): Observable<any> {
    return this.apiService.client.channels.keeprows.POST(tablesName).map((res) => {
      return this.apiService.handleResponse(res);
    });
  }

  whereclause(channelID: number, whereClause: string): Observable<any> {
    return this.apiService.client.channels.whereclause.POST(channelID, whereClause).map((res) => {
      return this.apiService.handleResponse(res);
    });
  }

  addColumns(channelID: number, columnName: string, aggregatedFunction: string): Observable<any> {
    return this.apiService.client.channels.addcolumns.POST(channelID, columnName, aggregatedFunction).map((res) => {
      return this.apiService.handleResponse(res);
    });
  }

  removedeletedcolumn(channelID: number, columnsName: Array<string>[]): Observable<any> {
    return this.apiService.client.channels.removedeletedcolumn.POST(channelID, columnsName).map((res) => {
      return this.apiService.handleResponse(res);
    });
  }

  groupByClause(channelID: number, columnsName: Array<string>[]): Observable<any> {
    return this.apiService.client.channels.groupbyclause.POST(channelID, columnsName).map((res) => {
      return this.apiService.handleResponse(res);
    });
  }

  applyTransform(channelID: number): Observable<any> {
    return this.apiService.client.channels.applytransform.GET(channelID).map((res) => {
      return this.apiService.handleResponse(res);
    });
  }

  addChannel(channelId: number, fieldRelationshipUc: string, dateReference: string, value: string): Observable<any> {
    return this.apiService.client.channels.addchannel.POST(channelId, fieldRelationshipUc, dateReference, value).map((res) => {
      return this.apiService.handleResponse(res);
    });
  }

  isChannelActive(channelID: number, channelContext: number, isActive: boolean): Observable<any> {
    // return this.apiService.client.channels.channelActive.GET(channelID, channelContext, isActive).map((res) => {
    //   return this.apiService.handleResponse(res);
    // });
    const channelPatch = {
      channelId: channelID,
      channelContext: channelContext,
      isActive: isActive
    }
    return this.httpClient.patch(`${this.BASE_URL}/channels/channelActive?channelId=${channelID}&channelContext=${channelContext}&isActive=${isActive}`
      , JSON.stringify(channelPatch)
      , { headers: new HttpHeaders(this.requestHeaders) });
  }

  getDatasetPreview(datasetId: number): Observable<any> {
    return this.apiService.client.channels.getdatasetpreview.GET(datasetId).map((res) => {
      return this.apiService.handleResponse(res);
    });
  }

  deleteDataset(datasetID: number): Observable<any> {
    return this.apiService.client.channels.deletedataset.DELETE(datasetID).map((res) => {
      return this.apiService.handleResponse(res);
    });
  }

  getPreviewAtFinalStep(aggregatedChannelId, fieldRelationshipUC, dateReference, value): Observable<any> {
    return this.apiService.client.channels.getpreviewfinalstep.GET(aggregatedChannelId, fieldRelationshipUC, dateReference, value).map((res) => {
      return this.apiService.handleResponse(res);
    });
  }

  getTransformGrid(aggregatedChannelId): Observable<any> {
    return this.apiService.client.channels.transformgrid.GET(aggregatedChannelId).map((res) => {
      return this.apiService.handleResponse(res);
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/core/api/channel.service.ts